import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isToday, isTomorrow, isFuture } from 'date-fns';
import { de } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import TrashEventModal from '../components/TrashEventModal';
import { parseIcsContent, detectTrashType } from '../utils/icsParser';

const { FiPlus, FiTrash2, FiEdit, FiX, FiUpload, FiDownloadCloud, FiLoader } = FiIcons;

export default function TrashCalendar() {
  const { state, dispatch } = useFamilyContext();
  const { trashCalendar } = state;
  
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  // Map types to direct colors for safer rendering
  const trashTypes = {
    restmüll: { color: 'bg-gray-700', hex: '#374151', icon: '🗑️', label: 'Restmüll' },
    biomüll: { color: 'bg-green-600', hex: '#16a34a', icon: '🥬', label: 'Biomüll' },
    papier: { color: 'bg-blue-600', hex: '#2563eb', icon: '📄', label: 'Papier' },
    gelberSack: { color: 'bg-yellow-500', hex: '#eab308', icon: '💛', label: 'Gelber Sack' },
    sperrmüll: { color: 'bg-orange-600', hex: '#ea580c', icon: '🪑', label: 'Sperrmüll' },
    schadstoff: { color: 'bg-red-600', hex: '#dc2626', icon: '☠️', label: 'Schadstoff' }
  };

  const upcomingEvents = (trashCalendar || [])
    .filter(event => new Date(event.date) >= new Date().setHours(0,0,0,0))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const todayEvents = (trashCalendar || []).filter(event => isToday(new Date(event.date)));
  const tomorrowEvents = (trashCalendar || []).filter(event => isTomorrow(new Date(event.date)));

  const handleAddEvent = (eventData) => {
    if (editingEvent) {
      dispatch({ type: 'UPDATE_TRASH_EVENT', payload: { ...editingEvent, ...eventData } });
      setEditingEvent(null);
    } else {
      dispatch({ type: 'ADD_TRASH_EVENT', payload: eventData });
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setShowModal(true);
  };

  const handleDeleteEvent = (eventId) => {
    if (confirm('Möchten Sie diesen Mülltermin wirklich löschen?')) {
      dispatch({ type: 'DELETE_TRASH_EVENT', payload: eventId });
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsImporting(true);
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const content = event.target.result;
        const events = parseIcsContent(content);
        
        let count = 0;
        // Filter only future events and convert to trash format
        events.forEach(icsEvent => {
          if (isFuture(icsEvent.start) || isToday(icsEvent.start)) {
            const type = detectTrashType(icsEvent.title);
            
            // Check for duplicates (simple check based on date and type)
            const exists = trashCalendar.some(ex => 
              ex.type === type && 
              new Date(ex.date).toDateString() === icsEvent.start.toDateString()
            );

            if (!exists) {
              dispatch({
                type: 'ADD_TRASH_EVENT',
                payload: {
                  type: type,
                  date: icsEvent.start.toISOString(),
                  notes: icsEvent.title !== type ? icsEvent.title : '', // Keep original title if it has info
                  reminder: true
                }
              });
              count++;
            }
          }
        });

        alert(`${count} Termine erfolgreich importiert!`);
      } catch (err) {
        alert('Fehler beim Import: ' + err.message);
      } finally {
        setIsImporting(false);
        // Reset file input
        e.target.value = null;
      }
    };

    reader.readAsText(file);
  };

  const getNextTrashDay = () => {
    const nextEvent = upcomingEvents[0];
    if (!nextEvent) return null;
    
    const eventDate = new Date(nextEvent.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = Math.abs(eventDate - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    
    return {
      ...nextEvent,
      dateObj: eventDate,
      daysUntil: diffDays,
      trashType: trashTypes[nextEvent.type] || trashTypes.restmüll
    };
  };

  const nextTrashDay = getNextTrashDay();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Müllabfuhrkalender</h1>
          <p className="text-gray-600 mt-2">
            Verwalten Sie alle Müllabfuhrtermine und erhalten Sie Erinnerungen
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
            {/* Import Button */}
            <label className={`flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer ${isImporting ? 'opacity-50 pointer-events-none' : ''}`}>
                {isImporting ? (
                    <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                    <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
                )}
                {isImporting ? 'Importiere...' : 'ICS Importieren'}
                <input type="file" accept=".ics" onChange={handleFileUpload} className="hidden" />
            </label>

            <button
                onClick={() => { setEditingEvent(null); setShowModal(true); }}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                Neuer Termin
            </button>
        </div>
      </div>

      {nextTrashDay && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl shadow-lg p-6 text-white relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${nextTrashDay.trashType.hex} 0%, #1f2937 100%)` }}
        >
           <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center space-x-4">
                  <div className="text-4xl">{nextTrashDay.trashType.icon}</div>
                  <div>
                      <h2 className="text-2xl font-bold">Nächste Müllabfuhr</h2>
                      <p className="text-lg opacity-90">
                          {nextTrashDay.trashType.label} • {format(nextTrashDay.dateObj, 'EEEE, dd. MMMM yyyy', { locale: de })}
                      </p>
                      <p className="text-sm opacity-75">
                         {isToday(nextTrashDay.dateObj) ? 'Heute' : isTomorrow(nextTrashDay.dateObj) ? 'Morgen' : `In ${Math.floor((nextTrashDay.dateObj - new Date()) / (1000 * 60 * 60 * 24))} Tagen`}
                      </p>
                  </div>
              </div>
              <button 
                onClick={() => handleEditEvent(nextTrashDay)}
                className="p-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiEdit} className="w-5 h-5" />
              </button>
           </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Heute</h3>
          {todayEvents.length > 0 ? (
            <div className="space-y-3">
              {todayEvents.map(event => {
                const trashType = trashTypes[event.type] || trashTypes.restmüll;
                return (
                  <div key={event.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 ${trashType.color} rounded-lg flex items-center justify-center text-white text-lg`}>
                        {trashType.icon}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{trashType.label}</p>
                        <p className="text-sm text-gray-500">{event.notes}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Heute keine Müllabfuhr</p>
          )}
        </motion.div>

        <motion.div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Morgen</h3>
          {tomorrowEvents.length > 0 ? (
            <div className="space-y-3">
               {tomorrowEvents.map(event => {
                const trashType = trashTypes[event.type] || trashTypes.restmüll;
                return (
                  <div key={event.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 ${trashType.color} rounded-lg flex items-center justify-center text-white text-lg`}>
                        {trashType.icon}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{trashType.label}</p>
                        <p className="text-sm text-gray-500">{event.notes}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Morgen keine Müllabfuhr</p>
          )}
        </motion.div>
      </div>

      <motion.div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Alle zukünftigen Termine</h3>
        {upcomingEvents.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {upcomingEvents.map(event => {
                const trashType = trashTypes[event.type] || trashTypes.restmüll;
                return (
                    <div key={event.id} className="flex items-center justify-between py-3">
                        <div className="flex items-center space-x-4">
                            <div className={`w-8 h-8 ${trashType.color} rounded-lg flex items-center justify-center text-white text-sm`}>
                                {trashType.icon}
                            </div>
                            <div>
                                <p className="font-medium text-gray-900">{trashType.label}</p>
                                <p className="text-sm text-gray-500">
                                    {format(new Date(event.date), 'EEEE, dd. MMMM yyyy', { locale: de })}
                                </p>
                            </div>
                        </div>
                        <div className="flex space-x-2">
                             <button onClick={() => handleEditEvent(event)} className="text-gray-400 hover:text-blue-600">
                                <SafeIcon icon={FiEdit} className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteEvent(event.id)} className="text-gray-400 hover:text-red-600">
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                );
            })}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Keine zukünftigen Termine geplant.</p>
        )}
      </motion.div>

      {showModal && (
        <TrashEventModal
          event={editingEvent}
          onClose={() => setShowModal(false)}
          onSave={handleAddEvent}
        />
      )}
    </div>
  );
}